import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { getWorkflowList } from '@/services/workflow/get-workflow-list';
import { validateCronExpression } from '@/lib/cron-utils';
import {
  useCreateSchedule,
  useUpdateSchedule,
} from '@/hooks/mutations/schedules/use-schedule-mutations';
import {
  buildCron,
  humanCron,
  parseCronToMode,
  pad2,
  HOURS_24,
  MINUTES,
  DAY_INTERVAL_NOTICE,
  nthCoverageNotice,
  NTH_OPTIONS,
  ordinalShort,
  intervalOptionsFor,
  DAY_LABELS,
  type ScheduleMode,
  type IntervalUnit,
  type ScheduleParams,
} from '@/pages/workflow/component/controls/schedule-helpers';
import type { OrgSchedule } from '@/types/schedules';

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const TIMEZONES = Array.from(
  new Set([
    LOCAL_TZ,
    'UTC',
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ]),
);

const OVERLAP_OPTIONS = [
  {
    value: 'skip',
    label: 'Skip',
    hint: 'Skip this run if a previous run is still active',
  },
  {
    value: 'allow',
    label: 'Allow',
    hint: 'Run anyway, alongside the active run',
  },
  {
    value: 'cancel_other',
    label: 'Cancel',
    hint: 'Cancel the active run and start this one',
  },
] as const;

interface ScheduleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: OrgSchedule | null;
  // When set, the modal creates a schedule for this specific workflow and hides
  // the picker (used from the workflow editor / list where the workflow is fixed).
  lockedWorkflowId?: string;
  lockedWorkflowName?: string;
  onSaved?: () => void;
}

const DEFAULTS = {
  mode: 'daily' as ScheduleMode,
  hour: 8,
  minute: 0,
  days: [1, 2, 3, 4, 5],
  dom: 1,
  intervalUnit: 'hour' as IntervalUnit,
  intervalN: 6,
  nth: 1,
  nthDow: 5, // first Friday -- the shape customers ask for most
};

export function ScheduleFormModal({
  open,
  onOpenChange,
  schedule,
  lockedWorkflowId,
  lockedWorkflowName,
  onSaved,
}: ScheduleFormModalProps) {
  const notification = useNotification();
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const isEdit = !!schedule;
  // The workflow is fixed when editing (it can't change) or when the caller
  // locks it; either way the picker is hidden and shown as a read-only field.
  const workflowLocked = isEdit || !!lockedWorkflowId;
  const lockedName = schedule?.workflow_name ?? lockedWorkflowName ?? '';

  const createMutation = useCreateSchedule();
  const updateMutation = useUpdateSchedule();

  const [name, setName] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [rawCron, setRawCron] = useState('0 8 * * *');
  const [mode, setMode] = useState<ScheduleMode>(DEFAULTS.mode);
  const [hour, setHour] = useState(DEFAULTS.hour);
  const [minute, setMinute] = useState(DEFAULTS.minute);
  const [days, setDays] = useState<number[]>(DEFAULTS.days);
  const [dom, setDom] = useState(DEFAULTS.dom);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(
    DEFAULTS.intervalUnit,
  );
  const [intervalN, setIntervalN] = useState(DEFAULTS.intervalN);
  const [nth, setNth] = useState(DEFAULTS.nth);
  const [nthDow, setNthDow] = useState(DEFAULTS.nthDow);
  const [timezone, setTimezone] = useState(LOCAL_TZ);
  const [overlapPolicy, setOverlapPolicy] = useState('skip');

  const applyParams = useCallback((p: ScheduleParams) => {
    setMode(p.mode);
    setHour(p.hour);
    setMinute(p.minute);
    setDays(p.days);
    setDom(p.dom);
    setIntervalUnit(p.intervalUnit);
    setIntervalN(p.intervalN);
    setNth(p.nth);
    setNthDow(p.nthDow);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      // One-shots have no cron and are not editable — the card hides Edit for
      // them, so this is a guard rather than a reachable path. Fall back to
      // the defaults instead of throwing inside parseCronToMode.
      const existingCron = schedule.cron_expression ?? '';
      const parsed = existingCron
        ? parseCronToMode(existingCron)
        : { ...DEFAULTS, rawCron: '' };
      setName(schedule.name || '');
      setWorkflowId(schedule.workflow_id);
      applyParams(parsed);
      setTimezone(schedule.timezone || LOCAL_TZ);
      setOverlapPolicy(schedule.overlap_policy || 'skip');
      setAdvanced(!!parsed.rawCron);
      setRawCron(parsed.rawCron || existingCron);
    } else {
      setName('');
      setWorkflowId(lockedWorkflowId ?? '');
      applyParams(DEFAULTS);
      setTimezone(LOCAL_TZ);
      setOverlapPolicy('skip');
      setAdvanced(false);
      setRawCron('0 8 * * *');
    }
  }, [open, schedule, lockedWorkflowId, applyParams]);

  const workflowsQuery = useQuery({
    queryKey: ['schedulable-workflows', orgId],
    queryFn: () => getWorkflowList(orgId!),
    enabled: !!orgId && open && !workflowLocked,
    staleTime: 60_000,
  });

  const schedulableWorkflows = useMemo(
    () =>
      (workflowsQuery.data?.data?.workflows ?? []).filter(
        (w) =>
          w.user_role === 'workflow:owner' || w.user_role === 'workflow:editor',
      ),
    [workflowsQuery.data],
  );

  const params: ScheduleParams = {
    mode,
    hour,
    minute,
    days,
    dom,
    intervalUnit,
    intervalN,
    nth,
    nthDow,
  };
  const cron = advanced ? rawCron : buildCron(params);
  const human = useMemo(() => {
    try {
      return advanced ? humanCron(parseCronToMode(rawCron)) : humanCron(params);
    } catch {
      return cron;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    advanced,
    rawCron,
    mode,
    hour,
    minute,
    days,
    dom,
    intervalUnit,
    intervalN,
    nth,
    nthDow,
  ]);

  // Day intervals restart their count each month; warn on both tabs, so a
  // hand-written `0 9 */3 * *` gets the same caveat as the builder.
  const showDayIntervalNotice = useMemo(() => {
    try {
      const p = advanced ? parseCronToMode(rawCron) : params;
      return p.mode === 'interval' && p.intervalUnit === 'day';
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanced, rawCron, mode, intervalUnit, intervalN]);

  // Warn only when the chosen occurrence is genuinely missing from some
  // months, so a 1st-4th selection never raises a false alarm.
  const nthNotice = useMemo(() => {
    try {
      const p = advanced ? parseCronToMode(rawCron) : params;
      if (p.mode !== 'nth') return null;
      return nthCoverageNotice(p.nth, p.nthDow);
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanced, rawCron, mode, nth, nthDow]);

  const localNow = new Date().toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  const utcNow = new Date().toLocaleString('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  });

  const toggleDay = (day: number) => {
    setDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async () => {
    if (!name.trim()) {
      notification.warning?.('Schedule name is required');
      return;
    }
    if (!isEdit && !workflowId) {
      notification.warning?.('Select a workflow');
      return;
    }
    if (!validateCronExpression(cron).valid) {
      notification.warning?.('Invalid cron expression');
      return;
    }

    try {
      if (isEdit && schedule) {
        await updateMutation.mutateAsync({
          workflowId: schedule.workflow_id,
          scheduleId: schedule.id,
          data: {
            name,
            cron_expression: cron,
            timezone,
            overlap_policy: overlapPolicy,
          },
        });
        notification.success('Schedule updated');
      } else {
        await createMutation.mutateAsync({
          workflowId,
          data: {
            name,
            cron_expression: cron,
            timezone,
            overlap_policy: overlapPolicy,
            enabled: true,
          },
        });
        notification.success('Schedule created');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'schedule-form-submit' },
      });
      notification.error('Failed to save schedule');
    }
  };

  // A cron edited by hand may carry an interval outside the preset list; keep
  // it selectable so the picker never blanks.
  const intervalOptions = useMemo(() => {
    const base = intervalOptionsFor(intervalUnit);
    return base.includes(intervalN)
      ? base
      : [...base, intervalN].sort((a, b) => a - b);
  }, [intervalUnit, intervalN]);

  const timezoneOptions = useMemo(
    () => (TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]),
    [timezone],
  );

  // The picker offers 1–28 (present in every month), but a cron edited to run
  // on day 29–31 must still show its value instead of blanking the select.
  const monthlyDayOptions = useMemo(() => {
    const base = Array.from({ length: 28 }, (_, i) => i + 1);
    return base.includes(dom) ? base : [...base, dom].sort((a, b) => a - b);
  }, [dom]);

  return (
    <Modal
      title={isEdit ? 'Edit Schedule' : 'Create Schedule'}
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      contentClassName='sm:max-w-[560px]'
      footer={
        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      }
    >
      <div className='space-y-5 px-3 py-1'>
        <div className='space-y-1.5'>
          <Label htmlFor='schedule-name'>Name</Label>
          <Input
            id='schedule-name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='i.e. Daily Morning Run'
          />
        </div>

        <div className='space-y-1.5'>
          <Label>Workflow</Label>
          {workflowLocked ? (
            <Input value={lockedName} disabled />
          ) : (
            <Select value={workflowId} onValueChange={setWorkflowId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    workflowsQuery.isLoading
                      ? 'Loading workflows…'
                      : 'Select a workflow'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {schedulableWorkflows.map((w) => (
                  <SelectItem key={w.workflow_id} value={w.workflow_id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <Label>Frequency</Label>
            <span className='rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground'>
              {human}
            </span>
          </div>

          <div className='flex w-full rounded-md border p-0.5'>
            {(['builder', 'cron'] as const).map((tab) => (
              <button
                key={tab}
                type='button'
                onClick={() => {
                  if (tab === 'cron') {
                    setRawCron(cron);
                    setAdvanced(true);
                    return;
                  }
                  const parsed = parseCronToMode(rawCron);
                  // A rawCron here means the expression can't be represented in
                  // the builder — stay on the Cron tab so we don't clobber it
                  // with builder defaults on save.
                  if (parsed.rawCron) return;
                  applyParams(parsed);
                  setAdvanced(false);
                }}
                className={cn(
                  'flex-1 rounded-[4px] py-1.5 text-sm font-medium capitalize transition-colors',
                  (tab === 'cron') === advanced
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          {advanced ? (
            <Input
              value={rawCron}
              onChange={(e) => setRawCron(e.target.value)}
              placeholder='0 8 * * *'
              className='font-mono'
            />
          ) : (
            <div className='space-y-2'>
              <div className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
                <span>Every</span>
                <Select
                  value={mode}
                  onValueChange={(v) => setMode(v as ScheduleMode)}
                >
                  <SelectTrigger className='h-8 w-auto gap-1'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='interval'>Interval</SelectItem>
                    <SelectItem value='hourly'>Hour</SelectItem>
                    <SelectItem value='daily'>Day</SelectItem>
                    <SelectItem value='weekdays'>Weekday</SelectItem>
                    <SelectItem value='weekly'>Week</SelectItem>
                    <SelectItem value='monthly'>Month</SelectItem>
                    <SelectItem value='nth'>Custom</SelectItem>
                  </SelectContent>
                </Select>

                {mode === 'interval' && (
                  <>
                    <NumberSelect
                      value={intervalN}
                      onChange={setIntervalN}
                      options={intervalOptions}
                    />
                    <Select
                      value={intervalUnit}
                      onValueChange={(v) => {
                        const unit = v as IntervalUnit;
                        const opts = intervalOptionsFor(unit);
                        setIntervalUnit(unit);
                        if (!opts.includes(intervalN)) setIntervalN(opts[0]);
                      }}
                    >
                      <SelectTrigger className='h-8 w-auto gap-1'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='minute'>Minutes</SelectItem>
                        <SelectItem value='hour'>Hours</SelectItem>
                        <SelectItem value='day'>Days</SelectItem>
                      </SelectContent>
                    </Select>
                    {intervalUnit === 'day' && (
                      <>
                        <span>at</span>
                        <NumberSelect
                          value={hour}
                          onChange={setHour}
                          options={HOURS_24}
                          fmt={pad2}
                        />
                        <span className='font-semibold text-foreground'>:</span>
                        <NumberSelect
                          value={minute}
                          onChange={setMinute}
                          options={MINUTES}
                          fmt={pad2}
                        />
                      </>
                    )}
                  </>
                )}

                {mode === 'hourly' && (
                  <>
                    <span>at</span>
                    <NumberSelect
                      value={minute}
                      onChange={setMinute}
                      options={MINUTES}
                      fmt={(v) => `:${pad2(v)}`}
                    />
                  </>
                )}

                {mode === 'weekly' && (
                  <>
                    <span>on</span>
                    <div className='flex flex-wrap gap-1'>
                      {DAY_LABELS.map((label, i) => {
                        const day = i + 1;
                        return (
                          <button
                            key={label}
                            type='button'
                            onClick={() => toggleDay(day)}
                            className={cn(
                              'rounded-md border px-2 py-1 text-xs',
                              days.includes(day)
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground',
                            )}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {mode === 'nth' && (
                  <>
                    <NumberSelect
                      value={nth}
                      onChange={setNth}
                      options={NTH_OPTIONS}
                      fmt={ordinalShort}
                    />
                    <Select
                      value={String(nthDow)}
                      onValueChange={(v) => setNthDow(parseInt(v, 10))}
                    >
                      <SelectTrigger className='h-8 w-auto gap-1'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAY_LABELS.map((label, i) => (
                          <SelectItem key={label} value={String(i + 1)}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span>of the month</span>
                  </>
                )}

                {mode === 'monthly' && (
                  <>
                    <span>on day</span>
                    <NumberSelect
                      value={dom}
                      onChange={setDom}
                      options={monthlyDayOptions}
                    />
                  </>
                )}

                {(mode === 'daily' ||
                  mode === 'weekdays' ||
                  mode === 'weekly' ||
                  mode === 'monthly') && (
                  <>
                    <span>at</span>
                    <NumberSelect
                      value={hour}
                      onChange={setHour}
                      options={HOURS_24}
                      fmt={pad2}
                    />
                    <span className='font-semibold text-foreground'>:</span>
                    <NumberSelect
                      value={minute}
                      onChange={setMinute}
                      options={MINUTES}
                      fmt={pad2}
                    />
                  </>
                )}
              </div>

              {mode === 'nth' && (
                <div className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
                  <span>at</span>
                  <NumberSelect
                    value={hour}
                    onChange={setHour}
                    options={HOURS_24}
                    fmt={pad2}
                  />
                  <span className='font-semibold text-foreground'>:</span>
                  <NumberSelect
                    value={minute}
                    onChange={setMinute}
                    options={MINUTES}
                    fmt={pad2}
                  />
                </div>
              )}
            </div>
          )}
          {showDayIntervalNotice && (
            <p className='text-xs text-muted-foreground'>
              {DAY_INTERVAL_NOTICE}
            </p>
          )}
          {nthNotice && (
            <p className='text-xs text-muted-foreground'>{nthNotice}</p>
          )}
        </div>

        <div className='space-y-1.5'>
          <Label>Timezone</Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timezoneOptions.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className='text-xs text-muted-foreground'>
            Local: {localNow}&nbsp;&nbsp;&nbsp;UTC: {utcNow}
          </p>
        </div>

        <div className='space-y-1.5'>
          <Label>If Previous run is still active</Label>
          <div className='flex w-full rounded-md border p-0.5'>
            {OVERLAP_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type='button'
                onClick={() => setOverlapPolicy(opt.value)}
                className={cn(
                  'flex-1 rounded-[4px] py-1.5 text-sm font-medium transition-colors',
                  overlapPolicy === opt.value
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className='text-xs text-muted-foreground'>
            {OVERLAP_OPTIONS.find((o) => o.value === overlapPolicy)?.hint}
          </p>
        </div>
      </div>
    </Modal>
  );
}

interface NumberSelectProps {
  value: number;
  onChange: (v: number) => void;
  options: number[];
  fmt?: (v: number) => string;
}

function NumberSelect({ value, onChange, options, fmt }: NumberSelectProps) {
  const format = fmt ?? ((v: number) => String(v));
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className='h-8 w-auto gap-1'>
        <SelectValue>{format(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent className='max-h-64'>
        {options.map((v) => (
          <SelectItem key={v} value={String(v)}>
            {format(v)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
